import { Table, CombinedTable, Reservation } from "@/types/table";
import { format, subDays } from "date-fns";
import { hasOvernight, serviceSortMinutes, type SlotBounds } from "@/lib/serviceOrder";
import { parseTimeSlotStart } from "@/lib/timeSlotUtils";
import { 
  doTimesOverlap, 
  parseTimeToMinutes, 
  normalizeTimeFormat,
  type BookingMode 
} from "@/lib/timeBookingUtils";

/**
 * Options for conflict detection, supporting both slot and time booking modes
 */
export interface ConflictCheckOptions {
  /** Booking mode: 'slot' for exact match, 'time' for duration-based overlap */
  bookingMode?: BookingMode;
  /** Duration of a reservation in minutes (used in 'time' mode) */
  reservationDurationMinutes?: number;
  /** Reservation ID to exclude from conflict detection (used in modification flows) */
  excludeReservationId?: string;
  /**
   * Créneaux de l'établissement. Utilisés UNIQUEMENT si l'un d'eux franchit minuit
   * (mode TIME) : les réservations de la veille qui débordent après minuit sont
   * alors prises en compte. Absent ou sans créneau de nuit → comportement historique.
   */
  serviceSlots?: SlotBounds[];
}

/**
 * Mode TIME + créneau de nuit : liste (réservation, début en minutes absolues
 * relatives au jour `date`) pour le jour et la veille. Renvoie null si inactif.
 */
function overnightTimeline(
  reservations: Reservation[],
  date: Date,
  options?: ConflictCheckOptions,
): { r: Reservation; start: number }[] | null {
  if ((options?.bookingMode ?? 'slot') !== 'time') return null;
  const slots = options?.serviceSlots;
  if (!hasOvernight(slots)) return null;
  const dayStr = format(date, "yyyy-MM-dd");
  const prevStr = format(subDays(date, 1), "yyyy-MM-dd");
  const out: { r: Reservation; start: number }[] = [];
  for (const r of reservations) {
    if (r.status === "annulé" || r.status === "en_attente") continue;
    if (r.id === options?.excludeReservationId) continue;
    const d = format(r.date, "yyyy-MM-dd");
    const base = serviceSortMinutes(normalizeTimeFormat(r.startTime), slots);
    if (d === dayStr) out.push({ r, start: base });
    else if (d === prevStr) out.push({ r, start: base - 1440 });
  }
  return out;
}

/**
 * Retourne l'ensemble des IDs de tables (simples et combinées) bloquées
 * pour une date et un créneau donnés.
 * 
 * In SLOT mode (default): Uses exact start time matching
 * In TIME mode: Uses duration-based overlap detection
 */
export function getBlockedTableIds(
  reservations: Reservation[],
  combinedTables: CombinedTable[],
  date: Date,
  startTime: string,
  options?: ConflictCheckOptions
): Set<string> {
  const blockedIds = new Set<string>();
  
  // Guard clauses pour éviter les erreurs
  if (!reservations || !Array.isArray(reservations)) return blockedIds;
  if (!combinedTables || !Array.isArray(combinedTables)) return blockedIds;
  if (!date || !startTime) return blockedIds;
  
  const dateStr = format(date, "yyyy-MM-dd");
  // Extraire uniquement l'heure de début du créneau (ex: "12:00:00 - 13:00:00" -> "12:00:00")
  const startTimeOnly = parseTimeSlotStart(startTime);
  
  // Default to slot mode for backward compatibility
  const bookingMode = options?.bookingMode ?? 'slot';
  const reservationDuration = options?.reservationDurationMinutes ?? 120;

  // Filter reservations for this date and non-cancelled status
  const dateReservations = reservations.filter(r => 
    format(r.date, "yyyy-MM-dd") === dateStr && 
    r.status !== "annulé" && r.status !== "en_attente" &&
    r.id !== options?.excludeReservationId
  );

  if (bookingMode === 'slot') {
    // SLOT MODE: Exact start time matching (original behavior)
    dateReservations
      .filter(r => r.startTime === startTimeOnly)
      .forEach(reservation => {
        // Si réservation sur table simple
        if (reservation.tableId) {
          blockedIds.add(reservation.tableId);
        }
        
        // Si réservation sur table combinée
        if (reservation.combinedTableId) {
          blockedIds.add(reservation.combinedTableId);
          
          // Bloquer TOUTES les tables individuelles de cette combinaison
          const combined = combinedTables.find(ct => ct.id === reservation.combinedTableId);
          if (combined) {
            combined.tableIds.forEach(tid => blockedIds.add(tid));
          }
        }
      });
  } else if (overnightTimeline(reservations, date, options)) {
    // TIME MODE + service de nuit : minutes absolues sur la veille et le jour
    const proposedStart = serviceSortMinutes(normalizeTimeFormat(startTimeOnly), options!.serviceSlots);
    overnightTimeline(reservations, date, options)!.forEach(({ r: reservation, start }) => {
      const existingDuration = reservation.durationMinutes ?? reservationDuration;
      const overlaps = proposedStart < start + existingDuration && start < proposedStart + reservationDuration;
      if (!overlaps) return;
      if (reservation.tableId) blockedIds.add(reservation.tableId);
      if (reservation.combinedTableId) {
        blockedIds.add(reservation.combinedTableId);
        const combined = combinedTables.find(ct => ct.id === reservation.combinedTableId);
        if (combined) combined.tableIds.forEach(tid => blockedIds.add(tid));
      }
    });
  } else {
    // TIME MODE: Duration-based overlap detection
    dateReservations.forEach(reservation => {
      // Use per-reservation duration if available, otherwise fall back to global
      const existingDuration = reservation.durationMinutes ?? reservationDuration;
      
      // Check if the proposed reservation would overlap with this existing reservation
      const overlaps = doTimesOverlap(
        normalizeTimeFormat(startTimeOnly),
        reservationDuration,
        normalizeTimeFormat(reservation.startTime),
        existingDuration
      );
      
      if (overlaps) {
        // Si réservation sur table simple
        if (reservation.tableId) {
          blockedIds.add(reservation.tableId);
        }
        
        // Si réservation sur table combinée
        if (reservation.combinedTableId) {
          blockedIds.add(reservation.combinedTableId);
          
          // Bloquer TOUTES les tables individuelles de cette combinaison
          const combined = combinedTables.find(ct => ct.id === reservation.combinedTableId);
          if (combined) {
            combined.tableIds.forEach(tid => blockedIds.add(tid));
          }
        }
      }
    });
  }

  // PROPAGATION: Bloquer toutes les tables combinées qui contiennent une table bloquée
  // Ceci garantit que si Table 3 est bloquée, "Table 1 + Table 3" sera aussi bloquée
  combinedTables.forEach(ct => {
    const hasBlockedTable = ct.tableIds.some(tid => blockedIds.has(tid));
    if (hasBlockedTable) {
      blockedIds.add(ct.id);
    }
  });

  return blockedIds;
}

/**
 * Vérifie si une table simple est disponible pour une réservation
 * (prend en compte les conflits de réservations et les combinaisons)
 */
export function isTableAvailableForSlot(
  table: Table,
  date: Date,
  startTime: string,
  reservations: Reservation[],
  combinedTables: CombinedTable[],
  options?: ConflictCheckOptions
): boolean {
  if (!table.isActive) return false;
  
  const blockedIds = getBlockedTableIds(reservations, combinedTables, date, startTime, options);
  return !blockedIds.has(table.id);
}

/**
 * Vérifie si une table combinée est disponible pour une réservation
 */
export function isCombinedTableAvailableForSlot(
  combinedTable: CombinedTable,
  date: Date,
  startTime: string,
  reservations: Reservation[],
  combinedTables: CombinedTable[],
  options?: ConflictCheckOptions
): boolean {
  if (!combinedTable.isActive) return false;
  
  const blockedIds = getBlockedTableIds(reservations, combinedTables, date, startTime, options);
  
  // La combinaison elle-même ne doit pas être bloquée
  if (blockedIds.has(combinedTable.id)) return false;
  
  // Aucune des tables de la combinaison ne doit être bloquée
  return !combinedTable.tableIds.some(tid => blockedIds.has(tid));
}

/**
 * Occupancy info: which reservation occupies a given table or combined table at a slot.
 * Used to surface "swap" opportunities in QuickTableChangeDialog.
 *
 * The returned Map keys are tableId OR combinedTableId. For an occupied combined table,
 * the map ALSO contains every member tableId pointing to the same reservation entry, and
 * every parent combined-table id whose membership includes a busy physical table.
 */
export interface OccupiedTableEntry {
  reservationLocalId: string;
  reservationSupabaseId?: string;
  customerName: string;
  numberOfPeople: number;
  time: string;
  tableId?: string;
  combinedTableId?: string;
  isLocked: boolean;
  status: Reservation["status"];
}

export function getOccupiedTableMap(
  reservations: Reservation[],
  combinedTables: CombinedTable[],
  date: Date,
  startTime: string,
  options?: ConflictCheckOptions,
): Map<string, OccupiedTableEntry> {
  const map = new Map<string, OccupiedTableEntry>();
  if (!reservations || !Array.isArray(reservations)) return map;
  if (!combinedTables || !Array.isArray(combinedTables)) return map;
  if (!date || !startTime) return map;

  const dateStr = format(date, "yyyy-MM-dd");
  const startTimeOnly = parseTimeSlotStart(startTime);
  const bookingMode = options?.bookingMode ?? 'slot';
  const reservationDuration = options?.reservationDurationMinutes ?? 120;

  const dateReservations = reservations.filter(r =>
    format(r.date, "yyyy-MM-dd") === dateStr &&
    r.status !== "annulé" && r.status !== "en_attente" &&
    r.id !== options?.excludeReservationId,
  );

  const night = overnightTimeline(reservations, date, options);
  const proposedNightStart = night ? serviceSortMinutes(normalizeTimeFormat(startTimeOnly), options!.serviceSlots) : 0;
  const overlapping = night
    ? night.filter(({ r, start }) => {
        const d = r.durationMinutes ?? reservationDuration;
        return proposedNightStart < start + d && start < proposedNightStart + reservationDuration;
      }).map(x => x.r)
    : dateReservations.filter(r => {
    if (bookingMode === 'slot') return r.startTime === startTimeOnly;
    const existingDuration = r.durationMinutes ?? reservationDuration;
    return doTimesOverlap(
      normalizeTimeFormat(startTimeOnly),
      reservationDuration,
      normalizeTimeFormat(r.startTime),
      existingDuration,
    );
  });

  overlapping.forEach(r => {
    const entry: OccupiedTableEntry = {
      reservationLocalId: r.id,
      reservationSupabaseId: r.supabaseId,
      customerName: `${r.customerFirstName ?? ""} ${r.customerLastName ?? ""}`.trim() || "Réservation",
      numberOfPeople: r.numberOfPeople,
      time: r.startTime,
      tableId: r.tableId,
      combinedTableId: r.combinedTableId,
      isLocked: !!r.isLocked,
      status: r.status,
    };
    if (r.tableId) {
      map.set(r.tableId, entry);
    }
    if (r.combinedTableId) {
      map.set(r.combinedTableId, entry);
      const ct = combinedTables.find(c => c.id === r.combinedTableId);
      if (ct) {
        ct.tableIds.forEach(tid => {
          if (!map.has(tid)) map.set(tid, entry);
        });
      }
    }
  });

  // Propagate to combined tables that share at least one busy physical table
  combinedTables.forEach(ct => {
    if (map.has(ct.id)) return;
    const busy = ct.tableIds.map(tid => map.get(tid)).find(Boolean);
    if (busy) map.set(ct.id, busy);
  });

  return map;
}

